import { type ReactNode, type RefObject, useCallback, useEffect, useRef, useState } from "react";

import { CancelIcon } from "@ala/icons/symbols/solid";

import { useResizeObserver } from "../../hooks/use-resize-observer";
import { clsx } from "../../utils/clsx";
import { type FocusTrap, FocusTrapContextProvider, createFocusTrap } from "../../utils/focus-trap";
import { Button } from "../button/button";
import { PortalContextProvider } from "../portal/portal-context";
import { ModalAriaContextProvider } from "./modal-aria-context";
import type { ModalBehaviorOptions, ModalCloseSource } from "./modal-behavior";
import { ModalContextProvider } from "./modal-context";

export const modalDivClassName = "modal-view";

const OFFSET_BETWEEN_MODALS = 24;
const MODAL_DEPTH_SCALE_FACTOR = 0.1;

interface ModalViewProps {
  children?: ReactNode;
  args?: unknown;
  hasCloseButton?: boolean;
  depth: number;
  onContentResize: () => void;
  onClose: (source: ModalCloseSource) => void;
  onBehaviorChange: (value: ModalBehaviorOptions) => void;
  renderStackOverlay: ReactNode;
  onPeekState: () => unknown;
  onStateChange: (value: unknown) => void;
}

export const ModalView = ({
  depth,
  children,
  hasCloseButton,
  args,
  onContentResize,
  onClose,
  onBehaviorChange,
  onPeekState,
  onStateChange,
  renderStackOverlay,
}: ModalViewProps) => {
  const contentDivRef = useRef<HTMLDivElement>(null);
  const portalDivRef = useRef<HTMLDivElement>(null);

  const [focusTrap, setFocusTrap] = useState<FocusTrap | null>(null);

  const [ariaLabelledById, setAriaLabelledById] = useState<string | undefined>(undefined);

  useResizeObserver(contentDivRef.current, onContentResize);

  useEffect(() => {
    if (!portalDivRef.current) {
      return;
    }

    const trap = createFocusTrap(portalDivRef.current, { allowOutsideClick: true, fallbackFocus: portalDivRef.current });
    trap.activate();

    setFocusTrap(trap);

    return () => {
      trap.deactivate();
      setFocusTrap(null);
    };
  }, [depth]);

  useEffect(() => {
    onContentResize();
  }, [onContentResize]);

  const closeModal = useCallback(() => {
    onClose("code");
  }, [onClose]);

  return (
    <ModalContextProvider
      value={{ args, closeModal, setBehaviorOptions: onBehaviorChange, peekState: onPeekState, setState: onStateChange }}
    >
      <ModalAriaContextProvider value={{ setLabelledById: setAriaLabelledById }}>
        <div className="flex h-6">
          {/** biome-ignore lint/a11y/noStaticElementInteractions: we need to trap some interactions from progressing to the modal backdrop */}
          <div
            className="mx-auto w-full overscroll-contain transition-all sm:w-fit"
            style={{
              scale: 1 - depth * MODAL_DEPTH_SCALE_FACTOR,
              minHeight: depth === 0 ? 0 : 48,
              clipPath: depth === 0 ? "none" : `xywh(0px 0px 100% ${OFFSET_BETWEEN_MODALS * 2}px)`,
            }}
            tabIndex={-1}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            ref={portalDivRef}
          >
            {renderStackOverlay}
            <div
              className={clsx(
                "relative z-10 h-[calc(100vh-64px)] overflow-hidden border border-contrast-100 bg-gray-50 shadow-[0px_24px_80px] shadow-contrast-100 transition-opacity outline-none select-text sm:h-fit",
                depth ? "opacity-0" : "opacity-100",
                modalDivClassName,
              )}
              role="dialog"
              aria-modal="true"
              aria-labelledby={ariaLabelledById}
              ref={contentDivRef}
            >
              <PortalContextProvider value={portalDivRef as RefObject<HTMLElement>}>
                <FocusTrapContextProvider value={focusTrap}>
                  {children}
                  {hasCloseButton && (
                    <Button
                      className="absolute top-6 right-6 p-2"
                      size="icon"
                      variant="ghost"
                      onClick={() => onClose("user")}
                      color="contrast"
                    >
                      <CancelIcon className="text-2xl" />
                    </Button>
                  )}
                </FocusTrapContextProvider>
              </PortalContextProvider>
            </div>
          </div>
        </div>
      </ModalAriaContextProvider>
    </ModalContextProvider>
  );
};
